import { TextAreaField } from '@aws-amplify/ui-react'
import { Alert, DialogContent, Stack } from '@mui/material'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomDialog from '@/components/feedback/CustomDialog'
import { ConfigReviewDialogActions } from '@/features/controller/configuration/components/ConfigReviewDialogActions'

type ConfigReviewDialogProps = {
  /** Reasons saving is not allowed, listed above the changes */
  blockingErrors?: string[]
  /** One {@link ConfigFileChangeReview} per changed file */
  children: ReactNode
  comment: string
  /** False when the draft already matches its base, so there is nothing to save */
  hasChanges: boolean
  isFetchingNewerVersion: boolean
  isUpdating: boolean
  onClose: () => void
  onCommentChange: (comment: string) => void
  onSave: () => void
  onSaveAndRestart: () => void
  open: boolean
  overwritesNewerVersion: boolean
}

/** The review before saving configuration, shared by the text editor and the configuration tree */
export function ConfigReviewDialog({
  blockingErrors = [],
  children,
  comment,
  hasChanges,
  isFetchingNewerVersion,
  isUpdating,
  onClose,
  onCommentChange,
  onSave,
  onSaveAndRestart,
  open,
  overwritesNewerVersion,
}: Readonly<ConfigReviewDialogProps>) {
  const { t } = useTranslation()

  return (
    <CustomDialog
      open={open}
      size="extraLarge"
      title={t('controllers.configuration.review_dialog.title')}
      onClose={onClose}
    >
      <DialogContent>
        <Stack gap={2}>
          {blockingErrors.map((error) => (
            <Alert key={error} severity="error">
              {error}
            </Alert>
          ))}
          {hasChanges ? (
            <>
              {children}
              <TextAreaField
                label={t('controllers.configuration.comment')}
                placeholder={t('controllers.configuration.comment_placeholder')}
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
              />
            </>
          ) : (
            <CustomTypography>{t('controllers.configuration.review_dialog.nothing_to_save')}</CustomTypography>
          )}
        </Stack>
      </DialogContent>
      <ConfigReviewDialogActions
        isLoading={isUpdating}
        isSaveLoading={isFetchingNewerVersion}
        overwritesNewerVersion={overwritesNewerVersion}
        saveDisabled={blockingErrors.length > 0 || !hasChanges}
        onCancel={onClose}
        onSave={onSave}
        onSaveAndRestart={onSaveAndRestart}
      />
    </CustomDialog>
  )
}
